import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe, Phone } from 'lucide-react';
import { companyInfo } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const AdvancedHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Services', nameZh: '服务', href: '#services' },
    { name: 'Support at Home', nameZh: '居家护理', href: '#funding' },
    { name: 'Your Journey', nameZh: '申请流程', href: '#journey' },
    { name: 'About', nameZh: '关于我们', href: '#about' },
    { name: 'Contact', nameZh: '联系', href: '#contact' }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-gray-100'
          : 'bg-white/80 backdrop-blur-md'
      }`}
      data-testid="site-header"
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-3">
        <div className="flex items-center justify-between">
          {/* Logo - Icon + Wordmark */}
          <a
            href="#home"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <div className="w-11 h-11 sm:w-12 sm:h-12 overflow-hidden rounded-full flex-shrink-0">
              <img
                src={companyInfo.logo}
                alt=""
                aria-hidden="true"
                className="h-full w-auto object-cover object-left transition-transform group-hover:scale-105"
                style={{ maxWidth: 'none' }}
              />
            </div>
            <div className="leading-none">
              <div className="font-bold text-navy-900 tracking-[0.15em] text-base sm:text-lg" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" }}>
                AU YH CARE
              </div>
              <div className="text-[10px] sm:text-[11px] text-gray-500 tracking-[0.2em] uppercase mt-1">
                Australian Home Care
              </div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-navy-800 hover:text-eucalyptus-600 font-medium text-[15px] transition-colors duration-200 relative group"
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                {language === 'zh' ? link.nameZh : link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-eucalyptus-500 transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-navy-700 hover:text-eucalyptus-600 font-medium text-sm transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              {language === 'en' ? '中文' : 'EN'}
            </button>

            {/* Phone */}
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="flex items-center gap-2 px-4 py-2 text-navy-800 hover:text-eucalyptus-600 font-semibold text-sm transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              {companyInfo.phone}
            </a>

            {/* CTA */}
            <a
              href="#contact"
              className="px-5 py-2.5 bg-navy-900 hover:bg-navy-800 text-white rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg"
              data-testid="header-cta"
            >
              {t('Book Consultation', '预约咨询')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="p-2 text-navy-700"
              aria-label={language === 'en' ? '切换到中文' : 'Switch to English'}
              data-testid="mobile-language-toggle"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-navy-800"
              aria-label={isMenuOpen ? t('Close menu', '关闭菜单') : t('Open menu', '打开菜单')}
              aria-expanded={isMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-6 space-y-3 border-t border-gray-100 mt-4">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-navy-800 hover:text-eucalyptus-600 font-medium text-lg py-2 transition-colors"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {language === 'zh' ? link.nameZh : link.name}
                  </a>
                ))}
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-2 py-2 text-navy-800 font-semibold"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
                <a
                  href="#contact"
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full px-6 py-3 bg-navy-900 text-white rounded-full font-semibold text-center transition-colors"
                >
                  {t('Book Consultation', '预约咨询')}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default AdvancedHeader;
